import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  Pagination,
  Skeleton,
  useMediaQuery,
  Alert
} from "@mui/material";

import { TicketService } from "../services/Ticket/Ticket";
import BackHeader from "../components/BackHeader";

import {
  setUser,
  setSessionExpired
} from "../Redux/Reducers/appState";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import usePendingPaymentRecovery from "../hooks/usePendingPaymentRecovery";

const statusColors = {
  active: { bg: "rgba(33,150,243,0.2)", color: "#42a5f5" },
  won: { bg: "rgba(76,175,80,0.2)", color: "#66bb6a" },
  lost: { bg: "rgba(244,67,54,0.2)", color: "#ef5350" },
};

const formatNaira = (amount) =>
  `₦${Number(amount || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;

export default function MyTickets() {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.appState.user);

  const isMobile = useMediaQuery("(max-width:600px)");

  const [tickets, setTickets] = useState([]);
  const [sortedTickets, setSortedTickets] = useState([]);
  const [currentPageTickets, setCurrentPageTickets] = useState([]);

  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const ticketsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  const { paymentAlert } = usePendingPaymentRecovery({
    expectedType: "ticket_purchase",
    onSuccess: async () => {
      await fetchTickets();
    }
  });

  

  const fetchTickets = async () => {

    setLoading(true);

    try {

      const response = await TicketService.getUserTickets();

      if (response?.message === "Invalid or expired token") {

        dispatch(setUser(null));
        dispatch(setSessionExpired(true));

        navigate("/login");
        return;
      }

      if (Array.isArray(response)) {

        setTickets(response);

        const pages = Math.max(Math.ceil(response.length / ticketsPerPage), 1);
        setTotalPages(pages);
        setPage(1);

      }

    } catch (err) {

      console.log("Error fetching tickets:", err);

    }

    setLoading(false);
  };

  useEffect(() => {

    if (user?.id) fetchTickets();

  }, []);

  

  useEffect(() => {

    const sorted = [...tickets].sort((a, b) => {

      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      return dateB - dateA;

    });

    setSortedTickets(sorted);

  }, [tickets]);

  

  useEffect(() => {

    const start = (page - 1) * ticketsPerPage;
    const end = start + ticketsPerPage;

    setCurrentPageTickets(sortedTickets.slice(start, end));

  }, [sortedTickets, page]);

  const ticketSummary = useMemo(() => {
    const totalTickets = tickets.length;
    const totalValue = tickets.reduce(
      (sum, ticket) => sum + Number(ticket.purchase_amount || 0),
      0
    );
    const activeTickets = tickets.filter(
      (ticket) => String(ticket.status || "").trim().toLowerCase() === "active"
    ).length;
    const expiredTickets = tickets.filter((ticket) => {
      const normalizedStatus = String(ticket.status || "").trim().toLowerCase();
      return normalizedStatus === "expired" || normalizedStatus === "lost";
    }).length;
    const uniqueDrawCodes = Array.from(
      new Set(
        tickets
          .map((ticket) => String(ticket?.Draw?.draw_code || "").trim())
          .filter(Boolean)
      )
    );

    return {
      totalTickets,
      totalValue,
      activeTickets,
      expiredTickets,
      singleDrawCode: uniqueDrawCodes.length === 1 ? uniqueDrawCodes[0] : "",
      hasMultipleDraws: uniqueDrawCodes.length > 1
    };
  }, [tickets]);

  

  const formatDate = (dateString) => {

    const date = new Date(dateString);

    if (isMobile) {

      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;

    }

    return date.toLocaleString("en-NG", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

  };

  return (

    <Box sx={{ p: { xs: 2, md: 3 } }}>

      <BackHeader title="My Tickets" />

      <Card
        sx={{
          mt: 2,
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          background: "linear-gradient(180deg,#0b2a4a,#071f36)",
          border: "1px solid rgba(255,255,255,0.08)"
        }}
      >
        <Stack spacing={2}>
          <Typography
            sx={{
              color: "white",
              fontWeight: 700,
              fontSize: { xs: "1.1rem", md: "1.25rem" }
            }}
          >
            My Tickets
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
              <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                Total Tickets Purchased
              </Typography>
              <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
                {ticketSummary.totalTickets} {ticketSummary.totalTickets === 1 ? "Ticket" : "Tickets"}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
              <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                Total Value
              </Typography>
              <Typography sx={{ color: "#22c55e", fontWeight: 700, fontSize: "1rem" }}>
                {formatNaira(ticketSummary.totalValue)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
              <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                Active Tickets
              </Typography>
              <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
                {ticketSummary.activeTickets}
              </Typography>
            </Box>

            {ticketSummary.hasMultipleDraws ? (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
                <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                  Expired Tickets
                </Typography>
                <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
                  {ticketSummary.expiredTickets}
                </Typography>
              </Box>
            ) : ticketSummary.singleDrawCode ? (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 180 } }}>
                <Typography sx={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                  Draw
                </Typography>
                <Typography sx={{ color: "white", fontWeight: 700, fontSize: "1rem" }}>
                  {ticketSummary.singleDrawCode}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </Card>

      {paymentAlert ? (
        <Alert severity={paymentAlert.severity} sx={{ mb: 2 }}>
          {paymentAlert.text}
        </Alert>
      ) : null}

      <Stack spacing={2}>

        {loading ? (

          [...Array(5)].map((_, i) => (

            <Skeleton
              key={i}
              height={90}
              sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1 }}
            />

          ))

        ) : (

          currentPageTickets.map((t) => {

            const style = statusColors[t.status] || statusColors.active;
            const winning = t.Winning;
            const settlementLabel = winning?.status === "paid" ? "SUCCESS" : "PENDING";

            return (

              <Card
                key={t.id}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  background: "linear-gradient(180deg,#0b2a4a,#071f36)",
                  border: "1px solid rgba(255,255,255,0.08)"
                }}
              >

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >

                  <Box>

                    <Typography
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        fontSize: "15px"
                      }}
                    >
                      {t.ticket_code}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#9ca3af",
                        fontSize: "12px"
                      }}
                    >
                      Draw {t.Draw.draw_code}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#9ca3af",
                        fontSize: "11px"
                      }}
                    >
                      {formatDate(t.created_at)}
                    </Typography>

                  </Box>

                  <Stack alignItems="flex-end" spacing={1}>

                    <Typography
                      sx={{
                        color: "#22c55e",
                        fontWeight: 700,
                        fontSize: "16px"
                      }}
                    >
                      ₦{Number(t.purchase_amount).toLocaleString()}
                    </Typography>

                    <Chip
                      label={t.status}
                      size="small"
                      sx={{
                        bgcolor: style.bg,
                        color: style.color,
                        fontWeight: 600,
                        fontSize: "11px",
                        textTransform: "capitalize"
                      }}
                    />

                    {winning ? (
                      <Box sx={{ textAlign: "right" }}>
                        <Typography sx={{ color: "#ffcc33", fontWeight: 700, fontSize: 13 }}>
                          WINNER • Prize {formatNaira(winning.amount)}
                        </Typography>
                        <Typography sx={{ color: settlementLabel === "SUCCESS" ? "#66bb6a" : "#ffca28", fontSize: 12, fontWeight: 700 }}>
                          Status: {settlementLabel}
                        </Typography>
                        {settlementLabel === "PENDING" && winning.settlement_available_at ? (
                          <Typography sx={{ color: "#b0bec5", fontSize: 11 }}>
                            Available {formatDate(winning.settlement_available_at)}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}

                  </Stack>

                </Stack>

              </Card>

            );

          })

        )}

      </Stack>

      <Stack alignItems="center" mt={3}>

        <Pagination
          size="small"
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          sx={{
            "& .MuiPaginationItem-root": { color: "white" },
            "& .Mui-selected": {
              bgcolor: "#4da3ff !important",
              color: "#001f3f !important"
            }
          }}
        />

      </Stack>

    </Box>
  );
}
